import {setViewIsDirty} from "../features/ux/uxSlice";
import {useDispatch} from "react-redux";

export const verifyLength = (value, original, length, required = true, setIsDirty = true) => {

  const dispatch = useDispatch();

  if (value && value.length >= length) {
    if (value !== original && setIsDirty) dispatch(setViewIsDirty(true));
    return true;
  } else if (!required && (!value || value.length === 0)) {
    if (value !== original && setIsDirty) dispatch(setViewIsDirty(true));
    return true;
  }
  return false;
};

export const isValidURL = (url) => {
  const urlPattern = new RegExp(
    '^' +
    '(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '(localhost|' + // localhost
    '((\\d{1,3}\\.){3}\\d{1,3})))' + // OR ip (v4) address
    '(\\:\\d+)?' + // port
    '(?!.*\\/{2})' + // REJECTION RULE: Negative Lookahead to reject two or more consecutive slashes
    '(\\/[-a-z\\d%_.~+]*)*' + // path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i'
  );
  return !!urlPattern.test(url);
};

export const isValidEmail = (email) => {
  const trimmedEmail = email?.trim() || '';
  const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailPattern.test(trimmedEmail);
};

export const INVALID_EMAIL_ERROR = 'The email is not valid';
export const DUPLICATE_EMAIL_ERROR =
  'Duplicate user email already exists. Enter a different email address.';

// Validate a user's email for both format and uniqueness within a client.
// Returns { valid, error }.
//
// A record only counts as a duplicate when it belongs to a *different* user: the
// user's own record is excluded by _id, and by their current email — the latter
// is what covers MIST-5468, where a brand-new user has no _id to match against
// once its just-created record reappears in clientUsers.
export const validateUserEmail = (
  value,
  currentUserEmail,
  clientUsers = [],
  currentUserId,
) => {
  const trimmedValue = value?.trim() || '';

  if (!isValidEmail(trimmedValue)) {
    return { valid: false, error: INVALID_EMAIL_ERROR };
  }

  const isDuplicate = clientUsers.some(
    (u) =>
      u._id !== currentUserId &&
      u.email_address?.toLowerCase() === trimmedValue.toLowerCase() &&
      trimmedValue.toLowerCase() !== currentUserEmail?.toLowerCase(),
  );

  if (isDuplicate) {
    return { valid: false, error: DUPLICATE_EMAIL_ERROR };
  }

  return { valid: true, error: '' };
};

// True when a rejected request was aborted rather than genuinely failed:
// fetchBaseQuery rejects with status 'TIMEOUT_ERROR' once its timeout elapses,
// and with a serialized AbortError when cancelled another way. Either way the
// backend may already have completed the work — for MIST-5468 the account was
// created and the invitation sent, yet the client showed "Failed to save" — so
// callers treat an abort as a successful save. A safety net now that the timeout
// is raised and the backend no longer blocks on SMTP.
export const isAbortError = (err) =>
  err?.status === 'TIMEOUT_ERROR' || err?.name === 'AbortError';
